'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { CollectionType, CommentType, ProductType, RecommendationType, UserType } from '@/@types';
import { useLocalizedLinks } from '@/components/core/hooks';
import FeedCard from './components/feed-card';
import FeedHeader from './components/feed-header';
import FeedFooter from './components/feed-footer';
import FeedSideBar from './components/feed-sidebar';
import FeedLoading from './components/feed-loading';
import { UIComponent } from '../..';
import { FeedContext, useFeedGroup } from './components/context/context';
import InternalLink from '../internal-link';
import FeedBackground from './components/feed-background';
import FeedAds from './components/feed-ads';

// eslint-disable-next-line no-unused-vars
type onFollowType = (user: UserType) => any;

const Feed: UIComponent<
  {
    comment?: CommentType;
    product: ProductType;
    user?: UserType;
    recommendation?: RecommendationType;
    collection?: CollectionType;
    priority?: boolean;
    onViewport?: CallableFunction;
    sizes?: string[];
    quality?: string | number;
  },
  'div',
  {
    Background: typeof FeedBackground;
    Card: typeof FeedCard;
    Header: typeof FeedHeader;
    Sidebar: typeof FeedSideBar;
    Footer: typeof FeedFooter;
    Loading: typeof FeedLoading;
    Ads: typeof FeedAds;
  }
> = ({ product, collection, user, comment, children, priority, recommendation, sizes, ...props }) => {
  const feedGroupContext = useFeedGroup();
  const [playing, setPlaying] = useState(false);
  const links = useLocalizedLinks();

  const contextValue = useMemo(() => {
    return {
      product,
      collection,
      user,
      comment,
      playing,
      priority,
      recommendation,
    };
  }, [product, collection, user, comment, playing, priority, recommendation]);

  useEffect(() => {
    setPlaying(feedGroupContext.playing === product.id);
  }, [feedGroupContext.playing, product.id]);

  return (
    <FeedContext.Provider value={contextValue}>
      <FeedCard
        product={product}
        recommendation={recommendation}
        playing={playing}
        sizes={sizes}
        onPlay={() => {
          return setPlaying(true);
        }}
        onStop={() => {
          return setPlaying(false);
        }}
        {...props}
      >
        {children || (
          <>
            <InternalLink href={links.product(product, comment?.id)} className="absolute inset-0 z-10" />
            <FeedHeader />
            <FeedSideBar />
            <FeedFooter />
          </>
        )}
      </FeedCard>
    </FeedContext.Provider>
  );
};

Feed.Background = FeedBackground;
Feed.Card = FeedCard;
Feed.Header = FeedHeader;
Feed.Sidebar = FeedSideBar;
Feed.Footer = FeedFooter;
Feed.Loading = FeedLoading;
Feed.Ads = FeedAds;

export default Feed;
